/**
 * @file tokens.js
 * @description LeadifyAI design tokens — the single source of truth for all
 * brand values used in the design system.
 *
 * Rules:
 *  - No MUI imports. This file is framework-agnostic.
 *  - No component logic. Only primitive values.
 *  - Every styled value in the app must originate from here.
 *  - When a value changes (e.g. brand refresh), update it once here only.
 *
 * Consumers:
 *  - theme/theme.js   — builds the MUI theme from these tokens
 *  - index.css        — CSS custom properties mirror these values for
 *                       non-React contexts (gradient orbs, utility classes)
 */

// =============================================================================
// COLORS
// =============================================================================

/**
 * Brand color palette.
 *
 * primary  — turquoise, the LeadifyAI brand accent. Used for primary actions,
 *             focus rings, checked states, and active indicators.
 *
 * secondary — sky blue, used for gradient backgrounds and decorative elements.
 *
 * accent    — soft pink, used sparingly in decorative / illustrative contexts.
 *
 * neutral   — Material Design gray scale. Used for text, borders, dividers,
 *             disabled states and neutral backgrounds.
 *
 * semantic  — standard status colors (error/success/warning/info).
 *             Deliberately NOT turquoise so they carry universal meaning.
 */
export const colors = {
  // ---------------------------------------------------------------------------
  // Primary — turquoise
  // ---------------------------------------------------------------------------
  primary: {
    main:  '#40E0D0',
    light: '#7FEEE0',
    dark:  '#00C9B0',

    // Named alpha variants — avoids scattered rgba() literals in components.
    // Usage: colors.primary.alpha10 instead of 'rgba(64, 224, 208, 0.10)'
    alpha03: 'rgba(64, 224, 208, 0.03)',
    alpha04: 'rgba(64, 224, 208, 0.04)',
    alpha05: 'rgba(64, 224, 208, 0.05)',
    alpha06: 'rgba(64, 224, 208, 0.06)',
    alpha07: 'rgba(64, 224, 208, 0.07)',
    alpha08: 'rgba(64, 224, 208, 0.08)',
    alpha10: 'rgba(64, 224, 208, 0.10)',
    alpha12: 'rgba(64, 224, 208, 0.12)',
    alpha14: 'rgba(64, 224, 208, 0.14)',
    alpha15: 'rgba(64, 224, 208, 0.15)',
    alpha18: 'rgba(64, 224, 208, 0.18)',
    alpha20: 'rgba(64, 224, 208, 0.20)',
    alpha22: 'rgba(64, 224, 208, 0.22)',
    alpha25: 'rgba(64, 224, 208, 0.25)',
    alpha28: 'rgba(64, 224, 208, 0.28)',
    alpha30: 'rgba(64, 224, 208, 0.30)',
    alpha32: 'rgba(64, 224, 208, 0.32)',
    alpha35: 'rgba(64, 224, 208, 0.35)',
    alpha40: 'rgba(64, 224, 208, 0.40)',
    alpha45: 'rgba(64, 224, 208, 0.45)',
    alpha50: 'rgba(64, 224, 208, 0.50)',
    alpha55: 'rgba(64, 224, 208, 0.55)',
    alpha60: 'rgba(64, 224, 208, 0.60)',

    // High-contrast accessible equivalents (WCAG AA on white)
    accessible:     '#005A52',
    accessibleDark: '#003D36',
  },

  // ---------------------------------------------------------------------------
  // Secondary — sky blue
  // ---------------------------------------------------------------------------
  secondary: {
    main:  '#87CEEB',
    light: '#B5E3F5',
    dark:  '#5AB5E3',

    // Named alpha variants — same convention as primary
    alpha03: 'rgba(135, 206, 235, 0.03)',
    alpha05: 'rgba(135, 206, 235, 0.05)',
    alpha08: 'rgba(135, 206, 235, 0.08)',
    alpha10: 'rgba(135, 206, 235, 0.10)',
    alpha15: 'rgba(135, 206, 235, 0.15)',
    alpha20: 'rgba(135, 206, 235, 0.20)',
    alpha28: 'rgba(135, 206, 235, 0.28)',
    alpha30: 'rgba(135, 206, 235, 0.30)',
  },

  // ---------------------------------------------------------------------------
  // Purple — soft violet, used as gradient endpoint paired with primary.
  // Not a standalone brand color; only appears in decorative gradients.
  // ---------------------------------------------------------------------------
  purple: {
    main:   '#F0ABFC',
    alpha03: 'rgba(240, 171, 252, 0.03)',
    alpha05: 'rgba(240, 171, 252, 0.05)',
  },

  // ---------------------------------------------------------------------------
  // Accent — soft pink (decorative / illustrative use only)
  // ---------------------------------------------------------------------------
  accent: {
    main:  '#FFB6C1',
    light: '#FFD6E1',
  },

  // ---------------------------------------------------------------------------
  // Analysis — elegant rose palette for the AI-analysis ("ניתוח AI") surface.
  // Deliberately mirrors the teal post-content panel's structure (same soft
  // 135deg gradient + blur + thin border) so the LeadCard / details-modal flip
  // reads as two faces of one object: teal on the front, rose on the back.
  // Replaced the former generic violet so the surface flows natively with the
  // dashboard's turquoise+pink language.
  // ---------------------------------------------------------------------------
  analysis: {
    gradient:
      'linear-gradient(135deg, rgba(255,241,242,0.55) 0%, rgba(253,242,248,0.55) 50%, rgba(255,228,230,0.55) 100%)',
    border:     'rgba(251,113,133,0.30)',  // rose-400 @30%
    borderSoft: 'rgba(244,63,94,0.15)',    // rose-500 @15% — dividers
    shadow:     '0 2px 12px rgba(244,63,94,0.08)',
    iconChip:   '#ffe4e6',                 // rose-100 — icon chip background
    icon:       '#e11d48',                 // rose-600 — Brain glyph (modal AI face)
    iconBright: '#fb4d6e',                 // bright rose/pink — Brain on the
                                           // LeadCard post window (eye-catching
                                           // against the teal panel)
    heading:    '#9f1239',                 // rose-800 — "ניתוח AI" label
    scrollThumb:'#fecdd3',                 // rose-200
    body:       '#1f2937',                 // neutral-800 — analysis text
  },

  // ---------------------------------------------------------------------------
  // Neutral gray scale (Material Design)
  // ---------------------------------------------------------------------------
  neutral: {
    50:  '#FAFAFA',
    100: '#F5F5F5',
    200: '#EEEEEE',
    300: '#E0E0E0',
    400: '#BDBDBD',
    500: '#9E9E9E',
    600: '#757575',
    700: '#616161',
    800: '#424242',
    900: '#212121',
  },

  // ---------------------------------------------------------------------------
  // Surface / background
  // ---------------------------------------------------------------------------
  background: {
    default: '#FFFFFF',
    paper:   '#F5F7FA',
  },

  // ---------------------------------------------------------------------------
  // Teal — slightly greener accent paired with brand turquoise.
  // Used in the Online Interventions KPI section and CTAs to bridge the
  // upper "metrics" zone with the lower "premium dossier" cards. Where the
  // brand primary leans cyan, this leans green — a deliberate sibling tone.
  // ---------------------------------------------------------------------------
  teal: {
    main:    '#14B8A6',  // teal-500 — base accent
    dark:    '#0D9488',  // teal-600 — CTA gradient endpoint
    darker:  '#0F766E',  // teal-700 — labels, deep accents, shadow tint
    light:   '#5EEAD4',  // teal-300 — soft hairline / hover lift
    alpha06: 'rgba(20, 184, 166, 0.06)',
    alpha08: 'rgba(20, 184, 166, 0.08)',
    alpha12: 'rgba(20, 184, 166, 0.12)',
    alpha18: 'rgba(20, 184, 166, 0.18)',
    alpha22: 'rgba(20, 184, 166, 0.22)',
    alpha30: 'rgba(20, 184, 166, 0.30)',
    alpha35: 'rgba(20, 184, 166, 0.35)',
    alpha40: 'rgba(20, 184, 166, 0.40)',
  },

  // ---------------------------------------------------------------------------
  // Intervention Feature Signature
  // ---------------------------------------------------------------------------
  // The dark-enterprise palette shared across every intervention touchpoint
  // so the feature reads as one cohesive product family:
  //   - Navbar item active state (Online Interventions)
  //   - LeadCard "Request Intervention" fingerprint button
  //   - Verified Intervention Card "Open Intervention Brief" CTA
  //   - Intervention Modal "View Full Thread" CTA
  //   - Verified Interventions page title underline
  // Composition: dark slate body + dark-teal accent + bright-cyan apex.
  // ---------------------------------------------------------------------------
  intervention: {
    bgStart:      '#FFFFFF',
    bgMid:        '#FAFBFC',
    bgPeak:       '#F6F8FA',
    accent:       '#00A896',
    accentBright: '#00A896',                    // no neon — matches fingerprint teal
    accentMuted:  'rgba(0, 168, 150, 0.40)',
    glow:         'rgba(0, 168, 150, 0.07)',
    glowHover:    'rgba(0, 168, 150, 0.12)',
    topEdge:      'rgba(0, 168, 150, 0.05)',
  },

  // ---------------------------------------------------------------------------
  // Business Intelligence Feature Signature
  // ---------------------------------------------------------------------------
  // The BI dossier shares the dark-enterprise body of the Interventions feature
  // (product-family coherence) but is signed in rose-pink instead of teal-cyan.
  // Used across:
  //   - Navbar item active state (Business Intelligence)
  //   - BI page title underline
  //   - BILeadCard banner hairline + CTA gradient border
  //   - BIMetrics KPI accents and per-mention bars
  //
  // Composition: same dark slate body + rose / fuchsia accent + bright rose apex.
  // The pink reads as "signal / alert" rather than candy — see Stripe Atlas,
  // Linear, surveillance dashboards for the same usage.
  // ---------------------------------------------------------------------------
  businessIntelligence: {
    // Banner / header darks — deliberately lifted to the same brightness range
    // as the Interventions surface tokens (`surface.deep` / `surface.edge`)
    // so the two premium dossier surfaces sit in the same tonal family.
    // The BI page's identity comes from the bottom hairline + CTA border +
    // page title underline, not from a darker body.
    bgStart:      '#212C42',  // indigo slate — same lightness as surface.deep
    bgMid:        '#272F48',  // intermediate, very faint warm shift
    bgPeak:       '#2E2F4D',  // brightest interior — subtle rose lift
    accent:       '#EC4899',  // fuchsia-500 — primary BI signature pink
    accentBright: '#F9A8D4',  // pink-300 — bright apex on dark
    accentDeep:   '#BE185D',  // pink-700 — heavy emphasis / labels
    accentMuted:  'rgba(236, 72, 153, 0.40)',
    glow:         'rgba(236, 72, 153, 0.10)',
    glowHover:    'rgba(236, 72, 153, 0.20)',
    topEdge:      'rgba(255, 255, 255, 0.05)', // ceramic top-edge — same as surface.topEdge

    // Sub-type accents — semantic / muted, NOT all-pink.
    // Pink stays the page signature (title, navbar, CTAs, KPI gradients) and
    // does not bleed into the data-viz layer where it competed with content.
    brandMention:      '#0D9488',  // teal-600 — your own brand (calm / owned)
    competitorMention: '#64748B',  // slate-500 — neutral observer (third-party)
    industryAlert:     '#F59E0B',  // amber-500 — alert / attention

    // Pink alpha utilities (signature surfaces only — KPI gradients, CTA glow).
    alpha04: 'rgba(236, 72, 153, 0.04)',
    alpha06: 'rgba(236, 72, 153, 0.06)',
    alpha08: 'rgba(236, 72, 153, 0.08)',
    alpha12: 'rgba(236, 72, 153, 0.12)',
    alpha18: 'rgba(236, 72, 153, 0.18)',
    alpha22: 'rgba(236, 72, 153, 0.22)',
    alpha30: 'rgba(236, 72, 153, 0.30)',
    alpha40: 'rgba(236, 72, 153, 0.40)',
  },

  // ---------------------------------------------------------------------------
  // Surface — dark enterprise (premium chrome, dossier cards)
  // Used for the Online Interventions experience and any future premium
  // surface that needs the brand's "command center" treatment.
  //
  // Tuned for *luxury restraint* rather than aggressive contrast. The palette
  // shifts within a narrow tonal range, with a subtle indigo-blue base and a
  // gentle teal hue lift along the gradient — the cue used by high-end dark
  // interiors (think dashboards in luxury cars / ceramic dials). Surfaces
  // pair these with:
  //   • a soft turquoise radial highlight in one corner (`highlightTint`)
  //   • an inner top-edge highlight (`topEdge`) — the "brushed-glass" cue
  // both consumed by intervention card / modal styling.
  // ---------------------------------------------------------------------------
  surface: {
    deep:          '#212C42',  // banner / header chrome (lighter, indigo-tinted)
    edge:          '#26334B',  // banner gradient endpoint — subtle teal lift
    dossier:       '#2A3853',  // dossier card body (reading-comfortable)
    dossierEnd:    '#33425F',  // dossier gradient endpoint — airier feel
    deeper:        '#1A2435',  // strongest dossier accent (seal fill, etc.)
    soft:          '#3A4970',  // hover lift on dark
    onDark:        '#E5EEF7',  // body text on dark surfaces
    onDarkMuted:   '#94A3B8',  // metadata text on dark surfaces
    // Soft turquoise radial overlay — adds dimension without saturating.
    highlightTint: 'rgba(64, 224, 208, 0.06)',
    // Inner top-edge highlight — the "ceramic / brushed-glass" cue.
    topEdge:       'rgba(255, 255, 255, 0.05)',
    alpha20:       'rgba(30, 41, 59, 0.20)',
    alpha40:       'rgba(30, 41, 59, 0.40)',
  },

  // ---------------------------------------------------------------------------
  // Text hierarchy
  // ---------------------------------------------------------------------------
  text: {
    primary:   '#212121',  // body copy
    secondary: '#757575',  // supporting text, metadata
    heading:   '#333333',  // h1–h6
    label:     '#666666',  // form labels, captions
  },

  // ---------------------------------------------------------------------------
  // Semantic state colors
  // Each has a solid main value and named alpha variants for backgrounds.
  // ---------------------------------------------------------------------------
  error: {
    main:    '#D32F2F',
    alpha10: 'rgba(244, 67, 54, 0.10)',
  },
  success: {
    main:    '#4CAF50',
    dark:    '#2E7D32',
    alpha12: 'rgba(76, 175, 80, 0.12)',
  },
  warning: {
    main:    '#FF9800',
    dark:    '#E65100',
    alpha12: 'rgba(255, 152, 0, 0.12)',
  },
  info: {
    main:    '#2196F3',
    hover:   '#1976D2',
    dark:    '#1565C0',
    alpha08: 'rgba(33, 150, 243, 0.08)',
    alpha12: 'rgba(33, 150, 243, 0.12)',
    alpha30: 'rgba(33, 150, 243, 0.30)',
  },
};

// =============================================================================
// BRAND ACCENT
// =============================================================================

/**
 * The deep brand teal and the two gradients built from it.
 *
 * WHY THIS EXISTS, AND WHY IT IS NOT A NEW COLOUR
 * `#0d9488` is the accent the product's chrome is actually signed in - 71 raw
 * literals inside `pages/settings/` alone, plus the homepage's `--hl-pro-deep`
 * (`src/homepage/homepage.css`). It is NOT `colors.primary.main`, which is the
 * brighter turquoise `#40E0D0`, so every `colors.primary.alphaNN` is a
 * different hue from the accent those literals paint beside it - which is
 * exactly why nobody reached for a token and typed the hex instead.
 *
 * The value was already in this file as `colors.teal.dark`; what was missing
 * was a name that says "this is the brand accent" rather than "this is a teal
 * in the teal ramp". So this is an ALIAS, not a second definition: change
 * `colors.teal` and this follows. Verified equal to `--hl-pro-deep` (#0d9488)
 * and to the hover/bright endpoints those gradients have always used.
 *
 * The two gradient strings are here because they are the literals that repeat
 * verbatim across the app's primary buttons - `135deg, accent -> bright`, and
 * `135deg, deep -> accent` on hover.
 */
export const brandAccent = {
  main:   colors.teal.dark,    // #0D9488 === homepage --hl-pro-deep
  bright: colors.teal.main,    // #14B8A6 — gradient endpoint
  deep:   colors.teal.darker,  // #0F766E — hover endpoint, AA-safe on tint

  gradient:      `linear-gradient(135deg, ${colors.teal.dark} 0%, ${colors.teal.main} 100%)`,
  gradientHover: `linear-gradient(135deg, ${colors.teal.darker} 0%, ${colors.teal.dark} 100%)`,
};

// =============================================================================
// TYPOGRAPHY
// =============================================================================

/**
 * Font families, weights, and the type scale.
 *
 * heading — Heebo. Used for h1–h6 and prominent labels. Chosen for strong
 *            Hebrew glyph coverage and a modern editorial voice. Poppins is
 *            kept as a Latin-only fallback for environments where Heebo is
 *            blocked (offline, strict CSP).
 * body    — DM Sans. Default for all body text. Pairs with Heebo at body
 *            sizes and also ships a quality Hebrew fallback.
 * mono    — Consolas stack. Used for code blocks only.
 */
export const typography = {
  fontFamily: {
    heading: '"Heebo", "Poppins", sans-serif',
    body:    '"DM Sans", "Open Sans", "Roboto", "Helvetica", "Arial", sans-serif',
    mono:    '"Consolas", "Monaco", "Courier New", monospace',
  },

  fontWeight: {
    regular:  400,
    medium:   500,
    semiBold: 600,
    bold:     700,
  },

  // Named size scale (px). MUI converts to rem internally.
  fontSize: {
    xs:   '12px',
    sm:   '14px',
    base: '16px',
    lg:   '18px',
    xl:   '20px',
    '2xl': '24px',
    '3xl': '28px',
    '4xl': '32px',
    '5xl': '36px',
  },

  lineHeight: {
    tight:   1.2,
    snug:    1.35,
    base:    1.5,
    relaxed: 1.6,
    loose:   1.8,
  },
};

// =============================================================================
// SHAPE / BORDER RADIUS
// =============================================================================

/**
 * Border-radius scale in px.
 * MUI's shape.borderRadius uses the md value as its base unit.
 *
 * sm   — chips, badges, inline code
 * md   — buttons, inputs, alerts (MUI base)
 * lg   — cards, papers, modals, drawers
 * xl   — pill / capsule elements
 * full — fully circular (avatars, FABs)
 */
export const radii = {
  sm:   8,
  md:   12,
  lg:   16,
  xl:   25,
  full: 9999,

  // Card asymmetric corners — top stays rounded, bottom is subtler.
  // Applied as per-corner strings in component sx (theme styleOverrides
  // don't support responsive breakpoint objects, so values live here and
  // are consumed directly in each card component).
  card: {
    xs: '36px 36px 16px 16px',
    sm: '48px 48px 20px 20px',
  },

  // Post content box inside cards — uniformly rounded, slightly tighter.
  contentBox: {
    xs: '10px',
    sm: '12px',
  },

  // Settings preference row (pages/settings/components/SettingsRow.js).
  //
  // ADDITIVE: the settings folder's surface scale is panel 16 / section 12 /
  // row 10, and the first two are already `lg` and `md`. Only the row value had
  // no name, which is why 86 raw radius literals accumulated in that folder.
  // Deliberately NOT aliased as `panel`/`section` as well - two names for one
  // number is how a scale stops being a scale.
  row: 10,
};

// =============================================================================
// SPACING
// =============================================================================

/**
 * Spacing scale in px. MUI's default spacing unit is 8px.
 * These named aliases are for use outside the MUI theme.spacing() function
 * (e.g. in CSS variables or plain style objects).
 */
export const spacing = {
  xs:   4,
  sm:   8,
  md:   16,
  lg:   24,
  xl:   32,
  '2xl': 48,
  '3xl': 64,
};

// =============================================================================
// SHADOWS
// =============================================================================

/**
 * Box-shadow definitions.
 * Neutral shadows use black; brand shadows use primary turquoise glow.
 */
export const shadows = {
  // Neutral elevation shadows
  sm:    '0 2px 4px rgba(0, 0, 0, 0.06)',
  md:    '0 4px 12px rgba(0, 0, 0, 0.08)',
  lg:    '0 8px 24px rgba(0, 0, 0, 0.10)',
  xl:    '0 12px 48px rgba(0, 0, 0, 0.12)',

  // Component-specific
  card:      '0 4px 20px rgba(0, 0, 0, 0.08)',
  cardHover: '0 8px 30px rgba(64, 224, 208, 0.15)',
  paper:     '0 2px 12px rgba(0, 0, 0, 0.06)',

  // Brand glow shadows (used on primary buttons)
  primaryGlow:      '0 4px 14px 0 rgba(64, 224, 208, 0.30)',
  primaryGlowHover: '0 6px 20px 0 rgba(64, 224, 208, 0.40)',
};

// =============================================================================
// TRANSITIONS
// =============================================================================

/**
 * Duration + easing presets.
 * fast  — micro-interactions (hover color changes)
 * base  — standard UI transitions (panels, cards)
 * slow  — orchestrated entrance animations
 */
export const transitions = {
  fast: '0.15s ease-in-out',
  base: '0.30s ease-in-out',
  slow: '0.50s ease-in-out',
};

// =============================================================================
// Z-INDEX LAYERS
// =============================================================================

/**
 * Named z-index stack.
 * Use these instead of arbitrary numbers to keep stacking context predictable.
 */
export const zIndex = {
  base:          1,
  dropdown:    100,
  sticky:      200,
  fixed:       300,
  modalBackdrop: 400,
  modal:       500,
  popover:     600,
  tooltip:     700,
};
